import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type ActivatedRouteSnapshot, Router } from '@angular/router';
import {
  catchError,
  filter,
  finalize,
  fromEvent,
  map,
  Observable,
  of,
  shareReplay,
  tap,
  throwError,
} from 'rxjs';

import { isTokenExpired } from '@core/utils/jwt.utils';

import { APP_ROUTES } from '@core/constants';
import { API_ENDPOINT, AUTH_STORAGE_KEY, USER_PROFILE_STORAGE_KEY } from '@core/configs';
import {
  PORTAL,
  USER_ROLE,
  type AuthState,
  type LoginRequestDto,
  type LoginResponseDto,
  type RefreshRequestDto,
  type StoredAuthSession,
  type TokenPairDto,
  type UserRole,
} from '@core/models/auth.models';
import { isRecord } from '@core/utils';
import { LocalStorageService } from './local-storage.service';
import { NewsService } from './news.service';

const USER_ROLES: readonly UserRole[] = [USER_ROLE.ADMIN, USER_ROLE.STAFF, USER_ROLE.CLIENT];

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(LocalStorageService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly newsService = inject(NewsService);
  private readonly state = signal<AuthState>({ status: 'anonymous' });
  private refreshInFlight$: Observable<StoredAuthSession> | null = null;

  readonly authState = this.state.asReadonly();
  readonly currentSession = computed(() => {
    const state = this.state();
    return state.status === 'authenticated' ? state.session : null;
  });
  readonly isAuthenticated = computed(() => this.currentSession() !== null);

  /**
   * CK PASS skip-login hands the user over as `?accessToken=…&refreshToken=…`.
   * The pair replaces any stored session; the returned Observable holds the
   * app initializer, so route guards already see the new session.
   */
  hydrateFromStorage(): Observable<unknown> | void {
    this.watchOtherTabs();
    const handoff = this.takeCkPassHandoff();
    if (!handoff) {
      this.restoreStoredSession();
      return;
    }
    return this.http.post<LoginResponseDto>(API_ENDPOINT.AUTH.SKIP_LOGIN, handoff).pipe(
      map((data) => this.acceptSession(toStoredAuthSession(data))),
      tap(() => this.newsService.clearCache()),
      catchError(() => {
        this.restoreStoredSession();
        return of(null);
      }),
    );
  }

  /** Reads the CK PASS token pair and strips it from the URL so it never lands in history. */
  private takeCkPassHandoff(): TokenPairDto | null {
    const view = this.document.defaultView;
    const href = view?.location.href;
    if (!view || !href) return null;
    const url = new URL(href);
    const accessToken = url.searchParams.get('accessToken');
    const refreshToken = url.searchParams.get('refreshToken');
    if (!accessToken || !refreshToken) return null;
    url.searchParams.delete('accessToken');
    url.searchParams.delete('refreshToken');
    view.history.replaceState(view.history.state, '', url.toString());
    return { accessToken, refreshToken };
  }

  private restoreStoredSession(): void {
    const stored = this.readStoredSession();
    if (!stored) {
      this.clearSession();
      return;
    }
    this.acceptSession(stored);

    // Proactively refresh an expired access token on startup so the first
    // API call doesn't have to wait for a 401 round-trip.
    if (isTokenExpired(stored.accessToken)) {
      this.refreshSession()
        .pipe(catchError(() => of(null)))
        .subscribe((refreshed) => {
          if (!refreshed) this.clearSession();
        });
    }
  }

  /**
   * A session whose role is outside `allowedRoles` is returned but never stored,
   * so a portal can reject it without other tabs ever seeing it in localStorage.
   */
  login(
    request: LoginRequestDto,
    allowedRoles: readonly UserRole[] = USER_ROLES,
  ): Observable<StoredAuthSession> {
    return this.http.post<LoginResponseDto>(API_ENDPOINT.AUTH.LOGIN, request).pipe(
      map((data) => toStoredAuthSession(data)),
      tap((session) => {
        if (allowedRoles.includes(session.role)) this.acceptSession(session);
      }),
      // A cache filled while logged out (public, non-admin news) must not
      // hide drafts from an admin who just logged in — see NewsService.
      tap(() => this.newsService.clearCache()),
    );
  }

  refreshSession(): Observable<StoredAuthSession> {
    this.refreshInFlight$ ??= this.requestRefresh().pipe(
      finalize(() => (this.refreshInFlight$ = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.refreshInFlight$;
  }

  private requestRefresh(): Observable<StoredAuthSession> {
    const current = this.currentSession();
    if (!current) {
      return throwError(() => new Error('AUTH_SESSION_MISSING'));
    }
    const request: RefreshRequestDto = {
      refreshToken: current.refreshToken,
      accessToken: current.accessToken,
    };
    return this.http.post<TokenPairDto>(API_ENDPOINT.AUTH.REFRESH, request).pipe(
      map((data) => this.acceptSession({ ...current, ...data })),
      catchError((error: unknown) => {
        // localStorage is shared: another tab may have rotated the refresh token
        // first, which makes ours stale. Adopt its result instead of logging out.
        const stored = this.readStoredSession();
        if (stored && stored.refreshToken !== current.refreshToken) {
          this.state.set({ status: 'authenticated', session: stored });
          return of(stored);
        }
        return throwError(() => error);
      }),
    );
  }

  /** Swaps in a new token pair (e.g. the U_SUPP_U pair from add-module) for the current session. */
  replaceTokens(tokens: TokenPairDto): void {
    const current = this.currentSession();
    if (current) this.acceptSession({ ...current, ...tokens });
  }

  logout(): Observable<void> {
    if (!this.currentSession()) {
      this.clearSession();
      return of(undefined);
    }
    return this.http.post<null>(API_ENDPOINT.AUTH.LOGOUT, null).pipe(
      map(() => undefined),
      finalize(() => this.clearSession()),
    );
  }

  clearSession(): void {
    this.storage.removeLocalData(AUTH_STORAGE_KEY);
    this.storage.removeLocalData(USER_PROFILE_STORAGE_KEY);
    this.state.set({ status: 'anonymous' });
    this.leaveProtectedRoute();
  }

  /**
   * Route guards only run on navigation, so a session cleared in place (logout,
   * 401 refresh failure) would leave the user sitting on a page they can no
   * longer access. Push them out of it.
   */
  private leaveProtectedRoute(): void {
    let target: string | null = null;
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (snapshot) {
      const data = snapshot.data as {
        portal?: string;
        allowAnonymous?: boolean;
        requiresAuth?: boolean;
      };
      if (data.portal === PORTAL.ADMIN && !data.allowAnonymous) {
        target = APP_ROUTES.ADMIN_LOGIN;
      } else if (data.requiresAuth) {
        target = APP_ROUTES.ROOT;
      }
      snapshot = snapshot.firstChild;
    }
    if (target) {
      void this.router.navigateByUrl(target);
    }
  }

  /**
   * The `storage` event fires only in the *other* tabs of this origin, so this
   * never reacts to its own writes. Same user → token rotation, adopt it in
   * place. Different identity (login, logout, user switch elsewhere) → reload
   * so route guards re-run and per-user caches (profile, news, rules) reset.
   */
  private watchOtherTabs(): void {
    const view = this.document.defaultView;
    if (!view) return;
    fromEvent<StorageEvent>(view, 'storage')
      .pipe(
        filter((event) => event.key === AUTH_STORAGE_KEY || event.key === null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const stored = this.readStoredSession();
        if (stored?.userId !== this.currentSession()?.userId) {
          view.location.reload();
          return;
        }
        this.state.set(
          stored ? { status: 'authenticated', session: stored } : { status: 'anonymous' },
        );
      });
  }

  private readStoredSession(): StoredAuthSession | null {
    const stored = this.storage.getLocalData(AUTH_STORAGE_KEY);
    return isStoredAuthSession(stored) ? stored : null;
  }

  private acceptSession(session: StoredAuthSession): StoredAuthSession {
    this.storage.setLocalData(AUTH_STORAGE_KEY, session);
    this.state.set({ status: 'authenticated', session });
    return session;
  }
}

function toStoredAuthSession(data: LoginResponseDto): StoredAuthSession {
  return {
    ...data,
    role: toUserRole(data.usuppuRole),
  };
}

function toUserRole(value: unknown): UserRole {
  if (typeof value === 'string') {
    const lower = value.toLowerCase() as UserRole;
    if (USER_ROLES.includes(lower)) {
      return lower;
    }
  }
  return USER_ROLE.CLIENT;
}

function isStoredAuthSession(value: unknown): value is StoredAuthSession {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value['accessToken'] === 'string' &&
    typeof value['refreshToken'] === 'string' &&
    typeof value['userId'] === 'number' &&
    USER_ROLES.includes(value['role'] as UserRole)
  );
}
